import {
  TwilioError,
  NetworkQualityLevel as TwilioNetworkQualityLevel,
  NetworkQualityStats as TwilioNetworkQualityStats,
  RemoteDataTrack as TwilioRemoteDataTrack,
  RemoteParticipant as TwilioRemoteParticipant,
  RemoteTrack as TwilioRemoteTrack,
  RemoteTrackPublication as TwilioRemoteTrackPublication,
  RemoteVideoTrack as TwilioRemoteVideoTrack,
  Track as TwilioTrack,
} from "twilio-video";
import { RemoteParticipantEvent } from "../../../core/room/participant/types";

/**
 * The base Participant events are shared between both Local and Remote Participant
 * https://github.com/twilio/twilio-video.js/blob/2.29.0/tsdef/Participant.d.ts
 */
type TwilioParticipantEventArgsMap = {
  reconnected: [participant: TwilioRemoteParticipant];
  reconnecting: [participant: TwilioRemoteParticipant];
  networkQualityLevelChanged: [
    networkQualityLevel: TwilioNetworkQualityLevel,
    networkQualityStats: TwilioNetworkQualityStats,
  ];
};

/**
 * Room and RemoteParticipant have slightly different function signatures for the same event.
 * https://github.com/twilio/twilio-video.js/blob/2.29.0/tsdef/RemoteParticipant.d.ts
 */
type TwilioRemoteParticipantEventArgsMap = TwilioParticipantEventArgsMap & {
  disconnected: [participant: TwilioRemoteParticipant];
  trackDimensionsChanged: [track: TwilioRemoteVideoTrack];
  trackDisabled: [publication: TwilioRemoteTrackPublication];
  trackEnabled: [publication: TwilioRemoteTrackPublication];
  trackMessage: [data: string | ArrayBuffer, track: TwilioRemoteDataTrack];
  trackPublished: [publication: TwilioRemoteTrackPublication];
  trackPublishPriorityChanged: [
    priority: TwilioTrack.Priority,
    publication: TwilioRemoteTrackPublication,
  ];
  trackStarted: [track: TwilioRemoteTrack];
  trackSubscribed: [
    track: TwilioRemoteTrack,
    publication: TwilioRemoteTrackPublication,
  ];
  trackSubscriptionFailed: [
    error: TwilioError,
    publication: TwilioRemoteTrackPublication,
  ];
  trackSwitchedOff: [
    track: TwilioRemoteTrack,
    publication: TwilioRemoteTrackPublication,
  ];
  trackSwitchedOn: [
    track: TwilioRemoteTrack,
    publication: TwilioRemoteTrackPublication,
  ];
  trackUnpublished: [publication: TwilioRemoteTrackPublication];
  trackUnsubscribed: [
    track: TwilioRemoteTrack,
    publication: TwilioRemoteTrackPublication,
  ];
};

export type TwilioRemoteParticipantEvent =
  keyof TwilioRemoteParticipantEventArgsMap;

/**
 * Twilio’s RemoteParticipant uses Node’s EventEmitter, which runs handlers
 * synchronously and ignores async work; does a "fire-and-forget".
 */
export type TwilioRemoteParticipantEventHandler = {
  [K in keyof TwilioRemoteParticipantEventArgsMap]: (
    ...args: TwilioRemoteParticipantEventArgsMap[K]
  ) => void;
};

/**
 * Contains the list of actively used standard events mapped to Twilio events.
 */
export const STANDARD_EVENT_TO_TWILIO_EVENT_MAP: Record<
  RemoteParticipantEvent,
  TwilioRemoteParticipantEvent
> = {
  mediaSubscribed: "trackSubscribed",
  mediaUnsubscribed: "trackUnsubscribed",
  mediaSwitchedOff: "trackSwitchedOff",
  mediaSwitchedOn: "trackSwitchedOn",
  mediaEnabled: "trackEnabled",
  mediaSubscriptionFailed: "trackSubscriptionFailed",
  networkQualityLevelChanged: "networkQualityLevelChanged",
};

export type StandardEventToTwilioEventMap =
  typeof STANDARD_EVENT_TO_TWILIO_EVENT_MAP;
